import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { RiskTemplateTagRow } from "./admin-risk-templates.js";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
/** A comma-joined value list on the wire, decoded to an array — the same CSV encoding `admin-risk-templates.ListRiskTemplatesParams`' filter fields use. */
const StringCsvList = S.transform(S.String, S.Array(S.String), {
    strict: true,
    decode: (s) => s
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    encode: (values) => values.join(","),
});
/**
 * `questions`/`questionnaires` are global catalogs (no `client_id`) used to
 * build onsite-inspection templates — gated the same way as
 * `admin-risk-templates`. `questionnaires.client_ids`/`assign_all` decide
 * which clients an inspection can pull a questionnaire from; that's an
 * inspection-assignment concern, not row ownership, so it is carried through
 * read-only here.
 */
export const QuestionTagRow = S.Struct({
    id: S.Int,
    translation_group_id: S.Int,
    value: S.String,
});
export const QuestionRiskTemplateLinkRow = S.Struct({
    id: S.UUID,
    translation_group_id: S.NullOr(S.Int),
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    status: S.NullOr(S.String),
    area: S.NullOr(S.String),
    object: S.NullOr(S.String),
    activity: S.NullOr(S.String),
    factor: S.NullOr(S.String),
    subfactor: S.NullOr(S.String),
    /**
     * The linked template's own `risk_template_tags`, aggregated into
     * `vw_questions.risk_templates` alongside the taxonomy values — the
     * question editor's linked-templates table shows tags in place of
     * environment/factor.
     */
    tags: S.Array(RiskTemplateTagRow),
});
export const QuestionQuestionnaireLinkRow = S.Struct({
    id: S.UUID,
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    type: S.NullOr(S.String),
    sort_order: S.NullOr(S.Int),
});
export const QuestionRow = S.Struct({
    id: S.UUID,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    title: S.String,
    description: S.NullOr(S.String),
    type_translation_group_id: S.NullOr(S.Int),
    type: S.NullOr(S.String),
    industry_translation_group_id: S.NullOr(S.Int),
    industry: S.NullOr(S.String),
    tags: S.Array(QuestionTagRow),
    risk_templates: S.Array(QuestionRiskTemplateLinkRow),
    questionnaires: S.Array(QuestionQuestionnaireLinkRow),
    linked_risk_count: S.Int,
    questionnaire_count: S.Int,
    created_by: S.NullOr(S.UUID),
    created_by_email: S.NullOr(S.String),
    created_by_first_name: S.NullOr(S.String),
    created_by_last_name: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
export const QuestionNotFound = S.Struct({
    _tag: S.Literal("QuestionNotFound"),
});
export const ListQuestionsParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
    // Text-equality filters on the view's own resolved type/industry
    // columns — the question-center's type/industry dropdowns match by
    // translated value, not by translation-group id.
    type: S.optional(StringCsvList),
    industry: S.optional(StringCsvList),
    // The upsert modal loads every language variant of one question by its
    // shared translation group — the same group id every language row
    // carries — rather than a single id.
    translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
}));
export const ListQuestions = HttpApiEndpoint.get("listQuestions", "/admin/questions")
    .setUrlParams(ListQuestionsParams)
    .addSuccess(pagedResponse(QuestionRow))
    .addError(Forbidden, { status: 403 });
export const GetQuestionParams = S.Struct({ id: S.UUID });
export const GetQuestion = HttpApiEndpoint.get("getQuestion", "/admin/questions/get")
    .setUrlParams(GetQuestionParams)
    .addSuccess(S.Struct({ data: QuestionRow }))
    .addError(QuestionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const CreateQuestionPayload = S.Struct({
    language: LanguageSchema,
    title: S.NonEmptyString,
    description: S.optional(S.String),
    translation_group_id: S.optional(S.Int),
    type_translation_group_id: S.optional(S.Int),
    industry_translation_group_id: S.optional(S.Int),
});
export const CreateQuestion = HttpApiEndpoint.post("createQuestion", "/admin/questions/create")
    .setPayload(CreateQuestionPayload)
    .addSuccess(S.Struct({ data: QuestionRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateQuestionPayload = S.Struct({
    id: S.UUID,
    title: S.optional(S.NonEmptyString),
    description: S.optional(S.NullOr(S.String)),
    type_translation_group_id: S.optional(S.NullOr(S.Int)),
    industry_translation_group_id: S.optional(S.NullOr(S.Int)),
});
export const UpdateQuestion = HttpApiEndpoint.post("updateQuestion", "/admin/questions/update")
    .setPayload(UpdateQuestionPayload)
    .addSuccess(S.Struct({ data: QuestionRow }))
    .addError(QuestionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteQuestionPayload = S.Struct({ id: S.UUID });
export const DeleteQuestion = HttpApiEndpoint.post("deleteQuestion", "/admin/questions/delete")
    .setPayload(DeleteQuestionPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(QuestionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const AddQuestionTagPayload = S.Struct({
    question_id: S.UUID,
    tag_group_id: S.Int,
});
export const AddQuestionTag = HttpApiEndpoint.post("addQuestionTag", "/admin/questions/tags/add")
    .setPayload(AddQuestionTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(QuestionNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveQuestionTagPayload = S.Struct({
    question_id: S.UUID,
    tag_group_id: S.Int,
});
export const RemoveQuestionTag = HttpApiEndpoint.post("removeQuestionTag", "/admin/questions/tags/remove")
    .setPayload(RemoveQuestionTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * Question-to-risk-template links key off translation groups (both sides
 * carry every language variant under one group id), unlike the tag link
 * above which keys off the question's own row id.
 */
export const AddQuestionRiskTemplateLinkPayload = S.Struct({
    question_translation_group_id: S.Int,
    risk_template_translation_group_id: S.Int,
});
export const AddQuestionRiskTemplateLink = HttpApiEndpoint.post("addQuestionRiskTemplateLink", "/admin/questions/risk-template-links/add")
    .setPayload(AddQuestionRiskTemplateLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const RemoveQuestionRiskTemplateLinkPayload = S.Struct({
    question_translation_group_id: S.Int,
    risk_template_translation_group_id: S.Int,
});
export const RemoveQuestionRiskTemplateLink = HttpApiEndpoint.post("removeQuestionRiskTemplateLink", "/admin/questions/risk-template-links/remove")
    .setPayload(RemoveQuestionRiskTemplateLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
/**
 * Raw `question_risk_template_links` edge — the join row itself, not the
 * joined-in template display data `QuestionRiskTemplateLinkRow` carries.
 * Consumed by the onsite-inspection sync engine to resolve which risk
 * templates a batch of cached questions link to.
 */
export const QuestionRiskTemplateLinkEdgeRow = S.Struct({
    id: S.Int,
    question_translation_group_id: S.Int,
    risk_template_translation_group_id: S.Int,
});
export const ListQuestionRiskTemplateLinksByQuestionGroupsPayload = S.Struct({
    question_translation_group_ids: S.Array(S.Int),
});
/**
 * Bulk-by-translation-group read for the sync engine's cache refresh — ids
 * ride the POST body since a device's cached question set can exceed a URL
 * length limit.
 */
export const ListQuestionRiskTemplateLinksByQuestionGroups = HttpApiEndpoint.post("listQuestionRiskTemplateLinksByQuestionGroups", "/admin/questions/risk-template-links/by-question-groups")
    .setPayload(ListQuestionRiskTemplateLinksByQuestionGroupsPayload)
    .addSuccess(S.Struct({ items: S.Array(QuestionRiskTemplateLinkEdgeRow) }))
    .addError(Forbidden, { status: 403 });
/**
 * The question-authoring lookup lists a question's type/industry/tag fields
 * resolve through — the `admin-questionnaires` analogue of
 * `admin-risk-catalog-values`' risk_template_* tables. All three share the
 * same `{id, language, translation_group_id, value}` shape.
 */
export const QuestionCatalogTable = S.Literal("questionnaire_types", "questionnaire_industries", "questionnaire_tags");
export const QuestionCatalogValueRow = S.Struct({
    id: S.Int,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    value: S.String,
});
export const ListQuestionCatalogValuesParams = S.extend(PagingParams, S.Struct({
    table: QuestionCatalogTable,
    language: S.optional(LanguageSchema),
    search: S.optional(S.String),
    // Resolves the row id for one already-known translation group in a
    // target language — how the upsert modal preselects a question's
    // existing type/industry/tag values when switching language tabs.
    translation_group_id: S.optional(S.NumberFromString.pipe(S.int())),
}));
export const ListQuestionCatalogValues = HttpApiEndpoint.get("listQuestionCatalogValues", "/admin/questions/catalog-values")
    .setUrlParams(ListQuestionCatalogValuesParams)
    .addSuccess(pagedResponse(QuestionCatalogValueRow))
    .addError(Forbidden, { status: 403 });
export const CreateQuestionCatalogValuePayload = S.Struct({
    table: QuestionCatalogTable,
    language: LanguageSchema,
    value: S.NonEmptyString,
    translation_group_id: S.optional(S.Int),
});
export const CreateQuestionCatalogValue = HttpApiEndpoint.post("createQuestionCatalogValue", "/admin/questions/catalog-values/create")
    .setPayload(CreateQuestionCatalogValuePayload)
    .addSuccess(S.Struct({ data: QuestionCatalogValueRow }))
    .addError(Forbidden, { status: 403 });
// ---------------------------------------------------------------------------
// questionnaires.*
// ---------------------------------------------------------------------------
export const QuestionnaireType = S.Literal("safety", "health");
export const QuestionnaireClientRow = S.Struct({
    id: S.UUID,
    name: S.NullOr(S.String),
    company_name: S.NullOr(S.String),
});
export const QuestionnaireQuestionRow = S.Struct({
    id: S.UUID,
    title: S.NullOr(S.String),
    description: S.NullOr(S.String),
    type: S.NullOr(S.String),
    industry: S.NullOr(S.String),
    sort_order: S.NullOr(S.Int),
    // Older deployed versions of `vw_questionnaires` omit the question's
    // translation_group_id. Keep this optional while rolling the view migration
    // through environments; current responses include it for cache refresh.
    translation_group_id: S.optional(S.NullOr(S.Int)),
});
export const QuestionnaireRow = S.Struct({
    id: S.UUID,
    language: LanguageSchema,
    translation_group_id: S.NullOr(S.Int),
    title: S.String,
    description: S.NullOr(S.String),
    reason: S.NullOr(S.String),
    type: QuestionnaireType,
    inspection_report_title: S.NullOr(S.String),
    assign_all: S.Boolean,
    client_ids: S.NullOr(S.Array(S.UUID)),
    tags: S.Array(QuestionTagRow),
    clients: S.NullOr(S.Array(QuestionnaireClientRow)),
    questions: S.Array(QuestionnaireQuestionRow),
    question_count: S.Int,
    assigned_client_count: S.NullOr(S.Int),
    created_by: S.NullOr(S.UUID),
    created_by_email: S.NullOr(S.String),
    created_by_first_name: S.NullOr(S.String),
    created_by_last_name: S.NullOr(S.String),
    date_created: S.NullOr(S.String),
    date_updated: S.NullOr(S.String),
});
export const QuestionnaireNotFound = S.Struct({
    _tag: S.Literal("QuestionnaireNotFound"),
});
export const ListQuestionnairesParams = S.extend(PagingParams, S.Struct({
    language: S.optional(LanguageSchema),
    type: S.optional(QuestionnaireType),
    search: S.optional(S.String),
}));
export const ListQuestionnaires = HttpApiEndpoint.get("listQuestionnaires", "/admin/questionnaires")
    .setUrlParams(ListQuestionnairesParams)
    .addSuccess(pagedResponse(QuestionnaireRow))
    .addError(Forbidden, { status: 403 });
export const ListQuestionnairesByTranslationGroupsPayload = S.Struct({
    translation_group_ids: S.Array(S.Int),
});
/**
 * Bulk-by-translation-group read for the sync engine's cache refresh: every
 * language variant of each requested questionnaire, in one round trip. Ids
 * ride the POST body since a device's active-inspection questionnaire set
 * can exceed a URL length limit.
 */
export const ListQuestionnairesByTranslationGroups = HttpApiEndpoint.post("listQuestionnairesByTranslationGroups", "/admin/questionnaires/by-translation-groups")
    .setPayload(ListQuestionnairesByTranslationGroupsPayload)
    .addSuccess(S.Struct({ items: S.Array(QuestionnaireRow) }))
    .addError(Forbidden, { status: 403 });
export const GetQuestionnaireParams = S.Struct({ id: S.UUID });
export const GetQuestionnaire = HttpApiEndpoint.get("getQuestionnaire", "/admin/questionnaires/get")
    .setUrlParams(GetQuestionnaireParams)
    .addSuccess(S.Struct({ data: QuestionnaireRow }))
    .addError(QuestionnaireNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const CreateQuestionnairePayload = S.Struct({
    language: LanguageSchema,
    title: S.NonEmptyString,
    description: S.optional(S.String),
    reason: S.optional(S.String),
    translation_group_id: S.optional(S.Int),
    type: S.optionalWith(QuestionnaireType, { default: () => "safety" }),
    inspection_report_title: S.optional(S.String),
    assign_all: S.optionalWith(S.Boolean, { default: () => false }),
    client_ids: S.optional(S.Array(S.UUID)),
});
export const CreateQuestionnaire = HttpApiEndpoint.post("createQuestionnaire", "/admin/questionnaires/create")
    .setPayload(CreateQuestionnairePayload)
    .addSuccess(S.Struct({ data: QuestionnaireRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateQuestionnairePayload = S.Struct({
    id: S.UUID,
    title: S.optional(S.NonEmptyString),
    description: S.optional(S.NullOr(S.String)),
    reason: S.optional(S.NullOr(S.String)),
    type: S.optional(QuestionnaireType),
    inspection_report_title: S.optional(S.NullOr(S.String)),
    assign_all: S.optional(S.Boolean),
    client_ids: S.optional(S.NullOr(S.Array(S.UUID))),
});
export const UpdateQuestionnaire = HttpApiEndpoint.post("updateQuestionnaire", "/admin/questionnaires/update")
    .setPayload(UpdateQuestionnairePayload)
    .addSuccess(S.Struct({ data: QuestionnaireRow }))
    .addError(QuestionnaireNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteQuestionnairePayload = S.Struct({ id: S.UUID });
export const DeleteQuestionnaire = HttpApiEndpoint.post("deleteQuestionnaire", "/admin/questionnaires/delete")
    .setPayload(DeleteQuestionnairePayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(QuestionnaireNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const AddQuestionnaireTagPayload = S.Struct({
    questionnaire_id: S.UUID,
    tag_group_id: S.Int,
});
export const AddQuestionnaireTag = HttpApiEndpoint.post("addQuestionnaireTag", "/admin/questionnaires/tags/add")
    .setPayload(AddQuestionnaireTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(QuestionnaireNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveQuestionnaireTagPayload = S.Struct({
    questionnaire_id: S.UUID,
    tag_group_id: S.Int,
});
export const RemoveQuestionnaireTag = HttpApiEndpoint.post("removeQuestionnaireTag", "/admin/questionnaires/tags/remove")
    .setPayload(RemoveQuestionnaireTagPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const AddQuestionnaireQuestionLinkPayload = S.Struct({
    questionnaire_id: S.UUID,
    question_translation_group_id: S.Int,
    sort_order: S.optional(S.Int),
});
export const AddQuestionnaireQuestionLink = HttpApiEndpoint.post("addQuestionnaireQuestionLink", "/admin/questionnaires/question-links/add")
    .setPayload(AddQuestionnaireQuestionLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ id: S.Int }) }))
    .addError(QuestionnaireNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const RemoveQuestionnaireQuestionLinkPayload = S.Struct({
    questionnaire_id: S.UUID,
    question_translation_group_id: S.Int,
});
export const RemoveQuestionnaireQuestionLink = HttpApiEndpoint.post("removeQuestionnaireQuestionLink", "/admin/questionnaires/question-links/remove")
    .setPayload(RemoveQuestionnaireQuestionLinkPayload)
    .addSuccess(S.Struct({ data: S.Struct({ removed: S.Int }) }))
    .addError(Forbidden, { status: 403 });
export const adminQuestionnairesGroup = HttpApiGroup.make("admin-questionnaires")
    .add(ListQuestions)
    .add(GetQuestion)
    .add(CreateQuestion)
    .add(UpdateQuestion)
    .add(DeleteQuestion)
    .add(AddQuestionTag)
    .add(RemoveQuestionTag)
    .add(AddQuestionRiskTemplateLink)
    .add(RemoveQuestionRiskTemplateLink)
    .add(ListQuestionRiskTemplateLinksByQuestionGroups)
    .add(ListQuestionCatalogValues)
    .add(CreateQuestionCatalogValue)
    .add(ListQuestionnaires)
    .add(GetQuestionnaire)
    .add(CreateQuestionnaire)
    .add(UpdateQuestionnaire)
    .add(DeleteQuestionnaire)
    .add(AddQuestionnaireTag)
    .add(RemoveQuestionnaireTag)
    .add(AddQuestionnaireQuestionLink)
    .add(RemoveQuestionnaireQuestionLink)
    .add(ListQuestionnairesByTranslationGroups)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-questionnaires.js.map